// <s-share> — one Share button that opens a small menu (WhatsApp, Facebook, Messenger, Copy link).
// Every option carries the product title and link (data-title / data-url on the element).
// Shares open in a new tab, popup or app so the shopper never leaves the store page; on phones
// WhatsApp opens the app directly (whatsapp://), which skips the "leave this site?" page.
//
// Markup contract:
//   [data-share-toggle]  — button that opens/closes the menu (aria-controls → menu id)
//   [data-share-menu]    — the menu panel (hidden attribute toggled)
//   [data-share="facebook" | "messenger" | "copy"] — options with extra behaviour
//   [data-share-status]  — polite live region for "Link copied" feedback
class SShare extends HTMLElement {
  #controller = null;
  #resetTimer = 0;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.toggleButton = this.querySelector("[data-share-toggle]");
    this.menu = this.querySelector("[data-share-menu]");
    this.status = this.querySelector("[data-share-status]");
    if (!this.toggleButton || !this.menu) return;

    this.toggleButton.addEventListener("click", () => this.#setOpen(this.menu.hidden), { signal });
    this.addEventListener("click", (event) => this.#onOptionClick(event), { signal });
    document.addEventListener(
      "click",
      (event) => {
        if (!this.menu.hidden && !this.contains(event.target)) this.#setOpen(false);
      },
      { signal },
    );
    this.addEventListener(
      "keydown",
      (event) => {
        if (event.key === "Escape" && !this.menu.hidden) {
          this.#setOpen(false);
          this.toggleButton.focus();
        }
      },
      { signal },
    );
  }

  disconnectedCallback() {
    this.#controller?.abort();
    clearTimeout(this.#resetTimer);
  }

  get #shareText() {
    return `${this.dataset.title} ${this.dataset.url}`;
  }

  get #isMobile() {
    return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) || window.matchMedia("(pointer: coarse)").matches;
  }

  #setOpen(open) {
    this.menu.hidden = !open;
    this.toggleButton.setAttribute("aria-expanded", String(open));
    if (open) this.menu.querySelector("a, button")?.focus();
  }

  async #onOptionClick(event) {
    const option = event.target.closest("[data-share]");
    if (!option || !this.contains(option)) return;
    const type = option.dataset.share;

    if (type === "copy") {
      event.preventDefault();
      await this.#copy();
      this.#announce(option, option.dataset.copiedLabel);
      return;
    }

    if (type === "whatsapp" && this.#isMobile) {
      // Open the WhatsApp app directly: app links skip the storefront's "leave this site?" page.
      event.preventDefault();
      this.#setOpen(false);
      window.location.href = `whatsapp://send?text=${encodeURIComponent(this.#shareText)}`;
      return;
    }

    if (type === "facebook") {
      event.preventDefault();
      this.#popup(option.href);
      this.#setOpen(false);
      return;
    }

    if (type === "messenger" && !this.#isMobile) {
      // Messenger has no app-free web share link on desktop: copy the title + link and open Messenger in a new tab.
      event.preventDefault();
      await this.#copy();
      window.open("https://www.messenger.com/", "_blank", "noopener");
      this.#announce(option, option.dataset.copiedLabel);
      return;
    }

    // WhatsApp and mobile Messenger follow their own links (new tab / app).
    this.#setOpen(false);
  }

  #popup(url) {
    const width = 600;
    const height = 560;
    const left = Math.max(0, (window.screen.width - width) / 2);
    const top = Math.max(0, (window.screen.height - height) / 2);
    const win = window.open(url, "lolonaa-share", `width=${width},height=${height},left=${left},top=${top}`);
    if (win) {
      win.opener = null;
    } else {
      window.open(url, "_blank", "noopener");
    }
  }

  async #copy() {
    const text = this.#shareText;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const field = document.createElement("textarea");
      field.value = text;
      field.setAttribute("readonly", "");
      field.style.position = "fixed";
      field.style.opacity = "0";
      document.body.append(field);
      field.select();
      document.execCommand("copy");
      field.remove();
    }
  }

  #announce(option, message) {
    if (!message) return;
    const label = option.querySelector("[data-share-label]");
    if (label) {
      label.dataset.original ??= label.textContent;
      label.textContent = message;
    }
    if (this.status) this.status.textContent = message;
    clearTimeout(this.#resetTimer);
    this.#resetTimer = setTimeout(() => {
      if (label?.dataset.original) label.textContent = label.dataset.original;
      if (this.status) this.status.textContent = "";
    }, 2500);
  }
}

if (!customElements.get("s-share")) customElements.define("s-share", SShare);
